// components/portal/PortalTemplate.tsx
import React, { FC, ReactElement, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SessionStore from "../../modules/session-store";
import { HiMenu, HiX } from "react-icons/hi";
import { ROUTES } from "../../routes";
import AppUserContext from "../../context/app-user-context";
import { PortalContext } from "../../context/portal-context";
import classnames from "classnames";
import UserHero from "./UserHero";
import UILoadingBlank from "../UILoadingBlank";
import MobileHeader from "./MobileHeader";
import { motion } from "framer-motion";
import CopyrightFooter from "../common/CopyrightFooter";
import TnCPopup from "../../modules/popup/TnCPopup";
import { useTncGate } from "../../context/useTncGate";
import PortalNav from "./PortalNav";

export type TPortalTemplate = { children: any };

const PortalTemplate: FC<TPortalTemplate> = ({ children }): ReactElement => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const user = useContext(AppUserContext);
  const { portalSetting, setPortalSetting } = useContext(PortalContext);

  // T&C gate (opens right after successful login if status is 0 or 2)
  const { shouldShow, setShouldShow, content, title } = useTncGate(user?.id as string | undefined, user?.logged);

  useEffect(() => {
    SessionStore.get("sidebarExpanded", sidebarExpanded).then((val) =>
      setSidebarExpanded(val)
    );
  }, []);

  return (
    <>
      <UILoadingBlank show={portalSetting && !portalSetting.ready} />

      <div
        className={classnames([
          "portal-template",
          { "pt--is-home": portalSetting.is_homepage },
          { "pt--is-not-home": !portalSetting.is_homepage },
          { "pt--mobile-show": portalSetting.mobile_nav_open },
          { "pt--bottom-nav": portalSetting.bottom_nav },
          { "pt--expanded": sidebarExpanded },
          { "pt--no-nav": !portalSetting.show_nav }
        ])}
      >
        <div className={classnames(["pt__sidebar", { "pt__sidebar--collapsed": !sidebarExpanded }])}>
          <div className="pt__sidebar-top">
            <div className="pt__logo">
              <Link to={ROUTES.PORTAL} onClick={() => setPortalSetting(d => ({ ...d, mobile_nav_open: false }))}>
                <img src="/assets/images/logo/logo-v2.png?v=2" alt="logo" height={68} />
                <img src="/assets/images/logo/logo-square-small-v2.png" alt="logo" height={68} />
              </Link>
            </div>

            <button
              className="toggle-btn"
              title={sidebarExpanded ? "Click here to collapse the menu" : "Click here to expand the menu"}
              onClick={(e) => {
                e.preventDefault();
                SessionStore.set("sidebarExpanded", !sidebarExpanded);
                setSidebarExpanded(!sidebarExpanded);
              }}
            >
              <HiMenu />
            </button>

            <button
              className="close-btn"
              title="Close menu"
              onClick={(e) => {
                e.preventDefault();
                setPortalSetting((d) => ({ ...d, mobile_nav_open: false }));
              }}
            >
              <HiX />
            </button>
          </div>

          <div className="pt__sidebar-menu">
            <PortalNav expanded={sidebarExpanded} />
          </div>
        </div>

        {portalSetting.mobile_nav_open && (
          <div className="pt__sidebar-overlay">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div
                className="so__content"
                onClick={(e) => {
                  e.preventDefault();
                  setPortalSetting((d) => ({ ...d, mobile_nav_open: false }));
                }}
              />
            </motion.div>
          </div>
        )}

        <div className="pt__header">
          <MobileHeader />
          <UserHero />
        </div>

        <div className="pt__content container-fluid d-flex flex-column" style={{ minHeight: "88vh" }}>
          <div className="flex-grow-1" style={{ minHeight: "calc(100% - 20px)" }}>
            {children}
          </div>
          <div className="pt-2">
            <CopyrightFooter
              align="right"
              onOpenTnC={() => {
                if (user?.logged) setShouldShow(true);
              }}
            />
          </div>
        </div>

        <PortalNav isBottomNav={true} />
      </div>

      {/* BLOCKING modal */}
      {user?.logged && <TnCPopup show={shouldShow} setShow={setShouldShow} api_content={content} api_title={title} />}
    </>
  );
};

export default PortalTemplate;
